import { useEffect, useState, useRef } from 'react';
import EjectionAnimation from './EjectionAnimation';
import AADefenseEffect from './AADefenseEffect';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

export default function AirStrikeAnimation({ sourceCountry, targetCountry, sourcePos, targetPos, success, onComplete, targetAATier, targetAADefenseLevel }) {
  const [phase, setPhase] = useState('takeoff'); // takeoff, flyingOut, hit1, hit2, hit3, bombing, turningAround, flyingBack, landing, ejecting
  const [bomberPos, setBomberPos] = useState({ x: sourcePos.x, y: sourcePos.y });
  const [missilePos, setMissilePos] = useState(null); // AA missile position (animated)
  const [showExplosion, setShowExplosion] = useState(false);
  const [bombs, setBombs] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [showEjection, setShowEjection] = useState(false);
  const [explosionFrame, setExplosionFrame] = useState(0); // 0 = none, 1 = hit, 2 = splitting, 3 = fireball
  const animationFrameRef = useRef(null);
  const hasInitialized = useRef(false);

  // 🐛 BUG FIX: Latest-onComplete ref so the main animation useEffect
  // doesn't re-fire (and restart all timers/audio) every time the parent
  // re-renders with a new inline arrow function.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Determine flight direction
  const isFlyingRight = targetPos.x > sourcePos.x;
  
  // Calculate overflight position (bomber flies PAST target, not stops on it)
  const overflightDistance = 15; // 15% past the target
  const flightVector = {
    x: targetPos.x - sourcePos.x,
    y: targetPos.y - sourcePos.y
  };
  const overflightPos = {
    x: targetPos.x + (flightVector.x > 0 ? overflightDistance : -overflightDistance),
    y: targetPos.y + (flightVector.y > 0 ? overflightDistance * 0.5 : -overflightDistance * 0.5)
  };
  
  // Bomber sprite selection based on source country and phase
  // For USA: Randomly select between Stealth Bomber and B-1 Bomber
  const [usaBomberType] = useState(() => Math.random() > 0.5 ? 'stealth' : 'b1');
  // For NK: Randomly select between Il-28 and Su-25
  const [nkBomberType] = useState(() => Math.random() > 0.5 ? 'il28' : 'su25');
  // For EU: Randomly select between F-35 Lightning II and F-16 Fighting Falcon
  const [euBomberType] = useState(() => Math.random() > 0.5 ? 'f35' : 'f16');
  
  const bomberMap = {
    'USA': usaBomberType === 'b1' ? {
      left: '/assets/bombers/b1-bomber-left.png',
      right: '/assets/bombers/b1-bomber-right.png',
      front: '/assets/bombers/b1-bomber-front.png',
      landing: '/assets/bombers/b1-bomber-landing.png'
    } : {
      left: '/assets/bombers/usa-stealth-left.png',
      right: '/assets/bombers/usa-stealth-right.png',
      front: '/assets/bombers/usa-stealth-front.png',
      landing: '/assets/bombers/usa-stealth-landing.png'
    },
    'RUS': {
      left: '/assets/bombers/russia-su34-left.png',
      right: '/assets/bombers/russia-su34-right.png',
      front: '/assets/bombers/russia-su34-front.png',
      landing: '/assets/bombers/russia-su34-landing.png'
    },
    'CHN': {
      left: '/assets/bombers/china-h6-left.png',
      right: '/assets/bombers/china-h6-right.png',
      front: '/assets/bombers/china-h6-front.png',
      landing: '/assets/bombers/china-h6-landing.png'
    },
    'EU': euBomberType === 'f35' ? {
      left: '/assets/bombers/eu/f35-left.png',
      right: '/assets/bombers/eu/f35-right.png',
      front: '/assets/bombers/eu/f35-front.png',
      landing: '/assets/bombers/eu/f35-landing.png'
    } : {
      left: '/assets/bombers/eu/f16-left.png',
      right: '/assets/bombers/eu/f16-right.png',
      front: '/assets/bombers/eu/f16-front.png',
      landing: '/assets/bombers/eu/f16-landing.png'
    },
    'NK': nkBomberType === 'su25' ? {
      left: '/assets/bombers/nk-su25-left.png',
      right: '/assets/bombers/nk-su25-right.png',
      front: '/assets/bombers/nk-su25-front.png',
      landing: '/assets/bombers/nk-su25-landing.png'
    } : {
      left: '/assets/bombers/nk-il28-left.png',
      right: '/assets/bombers/nk-il28-right.png',
      front: '/assets/bombers/nk-il28-front.png',
      landing: '/assets/bombers/nk-il28-landing.png'
    }
  };
  
  const countryBombers = bomberMap[sourceCountry] || bomberMap['USA'];
  
  // Explosion frames - ALL BOMBERS COMPLETE! 🎉
  const explosionFrames = {
    'USA-b1': {
      1: '/assets/bombers/b1-explosion-1.png',
      2: '/assets/bombers/b1-explosion-2.png',
      3: '/assets/bombers/b1-explosion-3.png'
    },
    'USA-stealth': {
      1: '/assets/bombers/stealth-explosion-1.png',
      2: '/assets/bombers/stealth-explosion-2.png',
      3: '/assets/bombers/stealth-explosion-3.png'
    },
    'EU-f16': {
      1: '/assets/bombers/f16-explosion-1.png',
      2: '/assets/bombers/f16-explosion-2.png',
      3: '/assets/bombers/f16-explosion-3.png'
    },
    'EU-f35': {
      1: '/assets/bombers/f35-explosion-1.png',
      2: '/assets/bombers/f35-explosion-2.png',
      3: '/assets/bombers/f35-explosion-3.png'
    },
    'NK-il28': {
      1: '/assets/bombers/il28-explosion-1.png',
      2: '/assets/bombers/il28-explosion-2.png',
      3: '/assets/bombers/il28-explosion-3.png'
    },
    'NK-su25': {
      1: '/assets/bombers/su25-explosion-1.png',
      2: '/assets/bombers/su25-explosion-2.png',
      3: '/assets/bombers/su25-explosion-3.png'
    },
    'CHN-h6': {
      1: '/assets/bombers/h6-explosion-1.png',
      2: '/assets/bombers/h6-explosion-2.png',
      3: '/assets/bombers/h6-explosion-3.png'
    },
    'RUS-su34': {
      1: '/assets/bombers/su34-explosion-1.png',
      2: '/assets/bombers/su34-explosion-2.png',
      3: '/assets/bombers/su34-explosion-3.png'
    }
  };
  
  // Determine explosion frame key
  let explosionKey = null;
  if (sourceCountry === 'USA') {
    explosionKey = usaBomberType === 'b1' ? 'USA-b1' : 'USA-stealth';
  } else if (sourceCountry === 'EU') {
    explosionKey = euBomberType === 'f16' ? 'EU-f16' : 'EU-f35';
  } else if (sourceCountry === 'NK') {
    explosionKey = nkBomberType === 'su25' ? 'NK-su25' : 'NK-il28';
  } else if (sourceCountry === 'CHN') {
    explosionKey = 'CHN-h6';
  } else if (sourceCountry === 'RUS') {
    explosionKey = 'RUS-su34';
  }
  
  // Select sprite based on phase and explosion state
  let bomberSprite;
  if (explosionFrame > 0 && explosionKey && explosionFrames[explosionKey]) {
    // Use explosion frame
    bomberSprite = explosionFrames[explosionKey][explosionFrame];
  } else if (phase === 'takeoff' || phase === 'landing') {
    bomberSprite = countryBombers.landing; // Use landing gear sprite
  } else if (phase === 'turningAround') {
    bomberSprite = countryBombers.front; // Use front view when turning
  } else if (phase === 'flyingBack') {
    bomberSprite = isFlyingRight ? countryBombers.left : countryBombers.right; // Reversed direction
  } else {
    bomberSprite = isFlyingRight ? countryBombers.right : countryBombers.left; // Normal direction
  }

  useEffect(() => {
    // Prevent double execution
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // 🔈 iOS audio channel: pause background music synchronously BEFORE
    // we try to play the siren so iOS WebKit hands us the audio channel.
    // Without this, our own siren / bomb / blast .play() calls silently
    // fail because the music is hogging the only HTML5 audio slot.
    claimAudioChannel();

    if (sourceCountry === 'USA') {
      console.log(`🛩️ AIR STRIKE INITIATED: USA launching ${usaBomberType === 'b1' ? 'B-1 BOMBER' : 'STEALTH BOMBER'}`);
    } else if (sourceCountry === 'NK') {
      console.log(`🛩️ AIR STRIKE INITIATED: NK launching ${nkBomberType === 'su25' ? 'SU-25 FROGFOOT' : 'IL-28 BOMBER'}`);
    } else if (sourceCountry === 'EU') {
      console.log(`🛩️ AIR STRIKE INITIATED: EU launching ${euBomberType === 'f35' ? 'F-35 LIGHTNING II' : 'F-16 FIGHTING FALCON'}`);
    } else {
      console.log('🛩️ AIR STRIKE INITIATED:', { source: sourceCountry, target: targetCountry, success });
    }

    // Play random air raid siren sound
    const sirenSounds = [
      '/assets/audio/air-raid-siren-1.mp3',
      '/assets/audio/air-raid-siren-2.mp3',
      '/assets/audio/air-raid-siren-3.mp3'
    ];
    const randomSiren = sirenSounds[Math.floor(Math.random() * sirenSounds.length)];
    const sirenSound = new Audio(randomSiren);
    sirenSound.volume = 0.7;
    sirenSound.play().catch(e => console.log('Air raid siren play failed:', e));
    console.log(`🚨 Playing air raid siren: ${randomSiren}`);

    // Country-specific AA missile sprites
    const AA_MISSILES = {
      'USA': '/assets/aa-missiles/stinger.png',
      'RUS': '/assets/aa-missiles/strela-2.png',
      'CHN': '/assets/aa-missiles/fn-6.png',
      'NK': '/assets/aa-missiles/ht-16pgj.png',
      'EU': '/assets/aa-missiles/mistral.png'
    };
    const missileSprite = AA_MISSILES[targetCountry] || AA_MISSILES['USA'];

    if (!success) {
      // ========== FAILURE PATH: INTERCEPTED BY AA ==========
      console.log('🎯 AIR STRIKE INTERCEPTED!');
      
      let collisionTriggered = false; // ✅ Prevent infinite loop
      
      // Calculate bomber's collision position (where bomber will be when missile hits at 3550ms)
      // Bomber starts flying at 1000ms and takes 5s to reach overflight (total 6000ms)
      // Missile hits at 3550ms, so bomber is at (3550 - 1000) / 5000 = 51% of its journey
      const collisionX = sourcePos.x + (targetPos.x - sourcePos.x) * 0.51;
      const collisionY = sourcePos.y + (targetPos.y - sourcePos.y) * 0.51;
      
      // Calculate AA missile START position (FAR SIDE of defending country)
      const missileOffsetX = targetPos.x > sourcePos.x ? 8 : -8;
      const missileOffsetY = targetPos.y > sourcePos.y ? 4 : -4;
      const missileStartX = targetPos.x + missileOffsetX;
      const missileStartY = targetPos.y + missileOffsetY;
      
      // Calculate angle from missile start to collision point
      const deltaX = collisionX - missileStartX;
      const deltaY = collisionY - missileStartY;
      const angleRad = Math.atan2(deltaY, deltaX);
      const angleDeg = angleRad * (180 / Math.PI);
      
      // ⭐ COUNTRY-SPECIFIC ROTATION OFFSETS (tested via /test-missile page)
      // Each missile sprite has a unique orientation requiring different offsets
      const missileRotationOffsets = {
        'USA': 50,  // Stinger
        'RUS': 47,  // Strela-2 (most aggressive forward tilt)
        'CHN': 51,  // FN-6
        'NK': 50,   // HT-16PGJ (same as USA)
        'EU': 51    // Mistral (same as China)
      };
      
      const rotationOffset = missileRotationOffsets[targetCountry] || 50;
      const adjustedAngle = rotationOffset - angleDeg;
      
      console.log(`🚀 AA Missile (${targetCountry}): Start(${missileStartX.toFixed(1)}%, ${missileStartY.toFixed(1)}%) → Collision(${collisionX.toFixed(1)}%, ${collisionY.toFixed(1)}%) at ${adjustedAngle.toFixed(0)}° (offset: ${rotationOffset}°, target: ${angleDeg.toFixed(0)}°)`);
      
      // Phase 1: Takeoff (1s)
      setTimeout(() => {
        console.log('✈️ Bomber taking off, flying straight through...');
        setPhase('flyingOut');
        // Fly PAST the target (keep going straight like success path)
        setBomberPos({ x: overflightPos.x, y: overflightPos.y });
        
        // AA Missile spawns at START position (far side of defender)
        setMissilePos({
          x: missileStartX,
          y: missileStartY,
          rotation: adjustedAngle,
          sprite: AA_MISSILES[targetCountry] || AA_MISSILES['USA'],
          shouldAnimate: false // Don't animate yet
        });
      }, 1000);
      
      // Phase 1.5: Fire missile toward collision point (immediate after spawn)
      setTimeout(() => {
        console.log('🚀 AA Missile launching toward bomber!');
        // Animate missile from start → collision point
        setMissilePos(prev => ({
          ...prev,
          x: collisionX,
          y: collisionY,
          shouldAnimate: true // Enable CSS transition
        }));
      }, 1050); // 50ms after missile appears
      
      // Phase 2: COLLISION after 2.5s missile flight (3550ms total)
      setTimeout(() => {
        if (!collisionTriggered) {
          collisionTriggered = true;
          console.log('💥 HIT! Frame 1 - Initial impact');
          setExplosionFrame(1); // Frame 1: Hit (bomber keeps flying)
          setMissilePos(null); // Missile disappears on impact
          
          // Explosion sound - REDUCED volume from 0.7 to 0.4
          const explosionSound = new Audio('/assets/audio/nuke-blast.mp3');
          explosionSound.volume = 0.4;  // ✅ REDUCED: Was 0.7 (too loud)
          explosionSound.play().catch(e => console.log('Explosion sound failed:', e));
        }
      }, 3550); // 1050ms (launch) + 2500ms (flight) = 3550ms
      
      // Frame 2: Splitting in half (0.625s after hit) - bomber keeps flying
      setTimeout(() => {
        console.log('💥 Frame 2 - Splitting in half');
        setExplosionFrame(2);
      }, 4175); // 3550 + 625
      
      // Frame 3: Fireball (0.625s after split) - bomber keeps flying
      setTimeout(() => {
        console.log('💥 Frame 3 - Fireball');
        setExplosionFrame(3);
      }, 4800); // 4175 + 625
      
      // Bomber continues flying straight through explosion (NO STOP)
      // It will reach overflight position naturally via CSS transition
      
      // Phase 5: Ejection (after fireball completes and bomber flies off-screen)
      setTimeout(() => {
        console.log('🪂 PILOT EJECTION');
        setPhase('ejecting');
        setShowEjection(true);
        sirenSound.pause();
      }, 8000); // Give plenty of time for fireball to finish
      
    } else {
      // ========== SUCCESS PATH: NORMAL BOMBING RUN ==========
      
      // Phase 1: Takeoff (1s)
      setTimeout(() => {
        console.log('✈️ Phase: FLYING TO TARGET');
        setPhase('flyingOut');
        // Fly PAST the target to overflight position
        setBomberPos({ x: overflightPos.x, y: overflightPos.y });
      }, 1000);
      
      // Phase 2: Drop bombs AS BOMBER PASSES OVER target (4s into flight, bomber is crossing target)
      setTimeout(() => {
        console.log('💣 Phase: BOMBING RUN (as bomber passes over target)');
        setPhase('bombing');
        
        // ✅ REDUCED VOLUME & FEWER SOUNDS: Drop 4 bombs but only play 2 explosion sounds (not 4)
        // This prevents overwhelming audio chaos while keeping visual bombs.
        // 🐛 BUG FIX (mobile audio): Create a FRESH Audio instance per bomb
        // explosion. iOS Safari won't restart a single Audio element that is
        // already playing — calling .play() twice on the same instance was
        // making bomb sounds silently fail on phones.
        for (let i = 0; i < 4; i++) {
          setTimeout(() => {
            setBombs(prev => [...prev, { id: Date.now() + i, x: targetPos.x, y: targetPos.y + 3 }]);
            
            setTimeout(() => {
              // ✅ REDUCED: Only play sound for first 2 bombs (not all 4)
              if (i < 2) {
                const bombSound = new Audio('/assets/audio/nuke-blast.mp3');
                bombSound.volume = 0.3;
                bombSound.play().catch(e => console.log('Bomb sound failed:', e));
              }
              setExplosions(prev => [...prev, { 
                id: Date.now() + i, 
                x: targetPos.x + (Math.random() - 0.5) * 8, 
                y: targetPos.y + (Math.random() - 0.5) * 8 
              }]);
            }, 300);
          }, i * 300);  // Faster bomb drops (300ms instead of 400ms)
        }
      }, 5000);  // Drop at 5s total (1s takeoff + 4s flight = 80% of journey, right over target)

      // Phase 3: Turn around at overflight position
      setTimeout(() => {
        console.log('🔄 Phase: TURNING AROUND (at overflight position)');
        setPhase('turningAround');
        // DON'T set position here - bomber is already at overflight from the initial 5s flight
      }, 6000);  // Turn starts at 6s (when flight completes)

      // Phase 4: Fly back to base from overflight position (3s flight)
      setTimeout(() => {
        console.log('🔙 Phase: RETURNING TO BASE from overflight position');
        console.log(`   Bomber at (${overflightPos.x}%, ${overflightPos.y}%) → returning to (${sourcePos.x}%, ${sourcePos.y}%)`);
        setPhase('flyingBack');
        setBomberPos({ x: sourcePos.x, y: sourcePos.y });
      }, 7500);  // Start return at 7.5s (1.5s turn duration)

      // Phase 5: Landing approach (after 3s return flight completes)
      setTimeout(() => {
        console.log('🛬 Phase: LANDING at home base');
        setPhase('landing');
      }, 10500);  // Landing at 10.5s (7.5s + 3s return flight)

      // Phase 6: Complete animation (give extra time for landing animation)
      setTimeout(() => {
        console.log('✅ AIR STRIKE COMPLETE - bomber safely landed');
        sirenSound.pause();
        onCompleteRef.current();
      }, 11500);  // Complete at 11.5s (after landing completes)
    }

    return () => {
      sirenSound.pause();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      hasInitialized.current = false; // CRITICAL: Reset on unmount
      // 🔈 Release the audio channel so background music resumes.
      releaseAudioChannel();
    };
  }, [sourceCountry, targetCountry, sourcePos, targetPos, success, isFlyingRight]);
  // 🐛 BUG FIX: Removed `onComplete` from deps. Captured via onCompleteRef.

  // ⭐ Determine bomber type for ejection animation
  const getBomberTypeForEjection = () => {
    if (sourceCountry === 'USA') return usaBomberType;
    if (sourceCountry === 'NK') return nkBomberType;
    if (sourceCountry === 'EU') return euBomberType;
    if (sourceCountry === 'RUS') return 'su34';
    if (sourceCountry === 'CHN') return 'h6';
    return 'b1';  // Default
  };

  // ⭐ Show ejection animation overlay
  if (showEjection) {
    console.log(`🎬 RENDERING EJECTION ANIMATION for ${sourceCountry} ${getBomberTypeForEjection()}`);
    return (
      <EjectionAnimation
        sourceCountry={sourceCountry}
        bomberType={getBomberTypeForEjection()}
        onComplete={onComplete}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* AA Missile (animated) - now with bright glow, no smoke trail */}
      {missilePos && !showExplosion && (
        <>
          {/* Main AA Missile with enhanced glow */}
          <img
            src={missilePos.sprite}
            alt="AA Missile"
            className="absolute"
            style={{
              left: `${missilePos.x}%`,
              top: `${missilePos.y}%`,
              width: '80px',
              height: 'auto',
              transform: `translate(-50%, -50%) rotate(${missilePos.rotation}deg)`,
              filter: 'drop-shadow(0 0 20px rgba(255,140,0,1)) brightness(1.2)',
              pointerEvents: 'none',
              zIndex: 100,
              transition: missilePos.shouldAnimate ? 'left 2.5s linear, top 2.5s linear' : 'none'
            }}
          />
        </>
      )}

      {/* Bomber Aircraft - Show damage/fire when intercepted */}
      {!showExplosion && (
        <>
          <img
            src={bomberSprite}
            alt={`${sourceCountry} Bomber`}
            className="absolute"
            style={{
              left: `${bomberPos.x}%`,
              top: `${bomberPos.y}%`,
              width: phase === 'takeoff' || phase === 'landing' ? '10%' : '12%',
              transform: `translate(-50%, -50%) ${phase === 'turningAround' ? 'rotateY(180deg)' : ''}`,
              filter: explosionFrame === 0 ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.6))' : 'none', // No filter during explosion
              opacity: phase === 'landing' && bomberPos.x === sourcePos.x && bomberPos.y === sourcePos.y ? 0 : 1,  // Only fade when fully landed
              transition: phase === 'flyingOut' || phase === 'bombing'
                ? 'all 5s ease-in-out'  // Success: slower, more cinematic flight
                : phase === 'turningAround'
                ? 'all 1.5s ease-in-out'
                : phase === 'flyingBack'
                ? 'all 3s ease-in-out'  // Slower return flight
                : phase === 'landing'
                ? 'all 0.5s ease-out, opacity 0.5s ease-out 0.5s'
                : 'none',
              zIndex: 10
            }}
          />
          
          {/* 🔥 Fire/Smoke Trail when intercepted (burning wreck effect) */}
          {phase === 'intercepted' && (
            <div
              className="absolute"
              style={{
                left: `${bomberPos.x}%`,
                top: `${bomberPos.y}%`,
                width: '100px',
                height: '100px',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(circle, rgba(255,69,0,0.8) 0%, rgba(255,140,0,0.5) 40%, transparent 70%)',
                filter: 'blur(10px)',
                animation: 'fireFlicker 0.3s infinite',
                zIndex: 9
              }}
            />
          )}
        </>
      )}
      
      {/* Bomber Shot Down Explosion */}
      {showExplosion && (
        <div
          className="absolute"
          style={{
            left: `${bomberPos.x}%`,
            top: `${bomberPos.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="relative">
            {/* Large explosion */}
            <div className="text-9xl animate-ping" style={{ animation: 'ping 1s ease-out' }}>💥</div>
            <div className="absolute inset-0 text-8xl animate-pulse">🔥</div>
            <div className="absolute inset-0 text-7xl animate-bounce">☁️</div>
          </div>
        </div>
      )}

      {/* Falling Bombs */}
      {bombs.map(bomb => (
        <div
          key={bomb.id}
          className="absolute text-2xl"
          style={{
            left: `${bomb.x}%`,
            top: `${bomb.y}%`,
            transform: 'translate(-50%, -50%)',
            animation: 'fall 0.3s linear forwards'
          }}
        >
          💣
        </div>
      ))}

      {/* Explosions */}
      {explosions.map(explosion => (
        <div
          key={explosion.id}
          className="absolute"
          style={{
            left: `${explosion.x}%`,
            top: `${explosion.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="relative">
            <div className="text-6xl animate-ping" style={{ animation: 'ping 0.5s ease-out' }}>💥</div>
            <div className="absolute inset-0 text-5xl" style={{ animation: 'pulse 0.5s ease-out' }}>🔥</div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes fall {
          from {
            transform: translate(-50%, -50%) translateY(0);
          }
          to {
            transform: translate(-50%, -50%) translateY(20px);
            opacity: 0;
          }
        }
      `}</style>
      <style>{`
        @keyframes fireFlicker {
          0%, 100% { 
            opacity: 0.7;
            transform: translate(-50%, -50%) scale(1);
          }
          50% { 
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.2);
          }
        }
      `}</style>
    </div>
  );
}
